import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { takePendingDemoTrip, resolveDemoTrip } from './utils/demoHandoff';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from './supabase';
import Login from './pages/Login';
import ResetPasswordScreen from './pages/ResetPasswordScreen';
import PinTrip from './pages/PinTrip';
import Dashboard from './pages/Dashboard';
import DealScreen from './pages/DealScreen';
import FreezeDetailScreen from './pages/FreezeDetailScreen';
import PaymentMethodScreen from './pages/PaymentMethodScreen';
import HotelDealScreen from './pages/HotelDealScreen';
import BookingOutcome from './pages/BookingOutcome';
import TripHistory from './pages/TripHistory';
import EmailChangeScreen from './pages/EmailChangeScreen';
import NotificationPreferencesScreen from './pages/NotificationPreferencesScreen';
import DeleteAccountScreen from './pages/DeleteAccountScreen';
import ChangeNameScreen from './pages/ChangeNameScreen';
import AppFooter from './AppFooter';
import './App.css';

// Extracted so useLocation() has a Router ancestor to read from — App()
// itself is what renders <Router>, so it can't call the hook directly.
function AppRoutes({ user, isPasswordRecovery, onPasswordReset }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Forces navigation to /reset-password the moment a real recovery
  // session is detected (see App()'s onAuthStateChange below) —
  // overrides wherever normal routing would otherwise have sent an
  // authenticated user (straight to /dashboard). Real gap found live
  // (Aug 31, per Ali's report): without this, clicking a Supabase
  // password-recovery email link created a valid session and just got
  // treated as an ordinary login, landing on the Dashboard with the
  // password never actually having been changed.
  useEffect(() => {
    if (isPasswordRecovery && location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true });
    }
  }, [isPasswordRecovery, location.pathname, navigate]);

  // Website demo handoff (Oct 2026): a visitor who pressed "Pin this trip" on
  // flyatlast.com arrives with the trip saved by captureDemoTripFromUrl().
  // Once they are logged in and on the dashboard, send them to the Pin Trip
  // form pre-filled. Never creates the pin itself.
  useEffect(() => {
    // Fires on ANY signed-in screen (not just /dashboard), so it still works if
    // the post-login redirect lands somewhere else. Skips the pin form itself.
    if (!user) return;
    const p = location.pathname;
    if (p === '/pin' || p === '/' || p === '/login' || p === '/reset-password') return;
    takePendingDemoTrip().then(async (trip) => {
      if (!trip) return;
      try {
        const demoTrip = await resolveDemoTrip(trip);
        navigate('/pin', { state: { demoTrip } });
      } catch (e) {
        navigate('/pin');
      }
    });
  }, [user, location.pathname, navigate]);

  // Deep-link handling for the native app (Aug 23, per Ali's report,
  // added for the Capacitor mobile build) — appUrlOpen only fires
  // inside a real native build, so this listener is simply inert on
  // web (registering it there costs nothing, it just never triggers).
  // When someone taps a referral link on their phone and it opens the
  // app instead of a browser, this is what actually routes them
  // in — without it, the app would just open to whatever screen it
  // happened to already be showing, silently dropping the referral.
  useEffect(() => {
    let listenerHandle;
    let cancelled = false;

    (async () => {
      const handle = await CapacitorApp.addListener('appUrlOpen', (event) => {
        // event.url is the full incoming URL — extract just the path
        // and query string and hand it to React Router directly rather
        // than a full page reload. Deliberately NOT re-parsing the
        // ?ref= param here — Login.js's own useEffect already does
        // that from window.location.search, so this only needs to
        // navigate there, not duplicate that logic a second time.
        try {
          const url = new URL(event.url);
          navigate(`${url.pathname}${url.search}`);
        } catch (e) {
          console.warn('Could not parse incoming deep link URL:', event.url, e);
        }
      });
      if (cancelled) {
        handle.remove();
      } else {
        listenerHandle = handle;
      }
    })();

    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, [navigate]);

  const showFooter = location.pathname !== '/' && location.pathname !== '/login';

  return (
    <>
    <Routes>
      {/* flyatlast.com now owns the marketing/pitch job — the app's
          own root route goes straight to Login instead of showing a
          second, redundant splash screen (the old Onboarding page).
          key={location.key} added (Aug 23, per Ali's report) for the
          same reason it's already used below on /pin and /freeze: a
          deep link arriving while already sitting on this route only
          changes the URL's query string, which React Router does NOT
          treat as reason enough to remount the component on its own —
          without a fresh key, Login's mount-only ?ref= useEffect simply
          wouldn't re-run, and the referral code would be silently
          dropped. */}
      <Route path="/" element={!user ? <Login key={location.key} /> : <Navigate to="/dashboard" />} />
      <Route path="/login" element={!user ? <Login key={location.key} /> : <Navigate to="/dashboard" />} />
      {/* key={location.key} forces a full remount every time someone
          navigates to /pin, even when it's the same route path as
          before (editing a pin, then later pinning a new trip, both
          hit "/pin"). Without this, React Router reuses the same
          PinTrip instance across navigations, and its useState
          initializers (form = { origin_code: prefill?.origin_code ||
          'YYZ', ... }) only ever run on the very first mount — so a
          field like origin_code could silently carry over from
          whatever prefill was present the first time PinTrip mounted
          in the session, onto a later, unrelated pin. This was the
          root cause of the origin/destination-swap bug (KHI showing
          up as origin_code on Toronto/Sydney pins after Karachi-
          related activity). location.key is a fresh value from React
          Router on every navigation, including ones to the same path,
          so this guarantees a truly clean component instance — fresh
          useState defaults, fresh refs — every time, rather than
          patching individual state fields one at a time. */}
      <Route path="/pin" element={user ? <PinTrip key={location.key} /> : <Navigate to="/login" />} />
      <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
      <Route path="/history" element={user ? <TripHistory /> : <Navigate to="/login" />} />
      {/* Account settings — Phase 2 (Sep 7, per Ali's report). Same
          user-required-or-redirect-to-login pattern as every other
          authenticated route here, nothing new architecturally. */}
      <Route path="/account/email" element={user ? <EmailChangeScreen /> : <Navigate to="/login" />} />
      <Route path="/account/name" element={user ? <ChangeNameScreen /> : <Navigate to="/login" />} />
      <Route path="/account/notifications" element={user ? <NotificationPreferencesScreen /> : <Navigate to="/login" />} />
      <Route path="/account/delete" element={user ? <DeleteAccountScreen /> : <Navigate to="/login" />} />
      {/* Only reachable via the isPasswordRecovery redirect above, not
          a normal nav link anywhere — see that effect's own comment.
          onDone clears the recovery flag in App() once the password
          is actually updated, so normal routing resumes afterward
          instead of bouncing back here forever. */}
      <Route path="/reset-password" element={user ? <ResetPasswordScreen onDone={onPasswordReset} /> : <Navigate to="/login" />} />
      <Route path="/deal" element={user ? <DealScreen /> : <Navigate to="/login" />} />
      {/* Opens right after a freeze is created (from either the Deal
          page pitch or the Dashboard's "Freeze this price" button), and
          is also the click-through target for any freeze card in
          FreezeTracker on the Dashboard. Takes only { freezeId, userId }
          via navigation state — it re-fetches the freeze itself so it
          always shows live, enriched data (current price, resolution
          status) rather than a stale snapshot from the moment it was
          created. */}
      {/* Two routes, same element (Sep 23, per Ali's report — the
          email deep-link fix): the plain /freeze path is the
          original in-app-navigation entry point (freezeId/userId
          come from router state, set by whatever screen sent the
          user here). /freeze/:freezeId is new — a real URL a person
          can open from outside the app (an email link, a bookmark),
          where FreezeDetailScreen resolves freezeId from the URL
          itself instead of relying on state that was never set. */}
      <Route path="/freeze" element={user ? <FreezeDetailScreen key={location.key} /> : <Navigate to="/login" />} />
      <Route path="/freeze/:freezeId" element={user ? <FreezeDetailScreen key={location.key} /> : <Navigate to="/login" />} />
      <Route path="/payment-method" element={user ? <PaymentMethodScreen /> : <Navigate to="/login" />} />
      <Route path="/hotel-deal" element={user ? <HotelDealScreen /> : <Navigate to="/login" />} />
      {/* Duffel Links redirects here on success/failure — not
          gated behind login, since the redirect happens in a new
          tab and we'd rather show the outcome than risk bouncing
          someone who just paid real money to a login screen. */}
      <Route path="/booking/success" element={<BookingOutcome success />} />
      <Route path="/booking/failed" element={<BookingOutcome success={false} />} />
    </Routes>
    {showFooter && <AppFooter />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // New (Aug 31, per Ali's report) — tracks whether the CURRENT session
  // came from a Supabase password-recovery link specifically, not just
  // "is someone logged in." onAuthStateChange fires a distinct
  // PASSWORD_RECOVERY event for this exact case, separate from the
  // ordinary SIGNED_IN event a real login produces — previously
  // ignored entirely, which was the root cause of recovery links
  // silently skipping the password-reset step.
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
    });
  }, []);

  if (loading) return <div className="screen" style={{justifyContent:'center', textAlign:'center'}}><p>Loading...</p></div>;

  return (
    <Router>
      <div className="app">
        <AppRoutes
          user={user}
          isPasswordRecovery={isPasswordRecovery}
          onPasswordReset={() => setIsPasswordRecovery(false)}
        />
      </div>
    </Router>
  );
}

export default App;